import { Link, Mail, Phone, Store, X } from 'lucide-react';
import { useState } from 'react';
import useEntity from '../hooks/useEntity';
import { entityService } from '../services/entityService';
import useToastStore from '../store/useToastStore';


interface ConfirmationModalProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: () => void;
  title: string;
  message: string;
  confirmText: string;
  cancelText: string;
}

const ConfirmationModal = ({ isOpen, onClose, onConfirm, title, message, confirmText, cancelText }: ConfirmationModalProps) => {
  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-lg max-w-md w-full p-6 relative">
        <button
          onClick={onClose}
          className="absolute top-4 right-4 text-gray-400 hover:text-gray-500"
        >
          <X className="w-5 h-5" />
        </button>
        <h3 className="text-lg font-medium text-gray-900 mb-2">{title}</h3>
        <p className="text-sm text-gray-600 mb-6">{message}</p>
        <div className="flex justify-end gap-3">
          <button
            onClick={onClose}
            className="px-4 py-2 text-sm font-medium text-gray-700 hover:text-gray-800 focus:outline-none"
          >
            {cancelText}
          </button>
          <button
            onClick={onConfirm}
            className="px-4 py-2 bg-blue-600 text-white text-sm font-medium rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
          >
            {confirmText}
          </button>
        </div>
      </div>
    </div>
  );
};

const Settings = () => {
  const { data: entity, refetch, isLoading: userLoading } = useEntity();
  const [isUpdating, setIsUpdating] = useState(false);
  const [newStoreUrl, setNewStoreUrl] = useState('');
  const [newMobileNumber, setNewMobileNumber] = useState('');
  const [showStatusModal, setShowStatusModal] = useState(false);
  const [showUrlModal, setShowUrlModal] = useState(false);
  const [showMobileModal, setShowMobileModal] = useState(false);
  const [isEditingUrl, setIsEditingUrl] = useState(false);
  const [isEditingMobile, setIsEditingMobile] = useState(false);
  const toast = useToastStore();

  const handleToggleStoreStatus = async () => {
    try {
      setIsUpdating(true);
      await entityService.updateEntityStoreStatus(!entity!.isActive);
      await refetch();
      setShowStatusModal(false);
      toast.showToast(
        `Store successfully ${entity?.isActive ? 'deactivated' : 'activated'}.`,
        'success'
      );
    } catch (error) {
      setShowStatusModal(false);
      console.error('Error toggling store status:', error);
      toast.showToast(
        error instanceof Error ? error.message : 'An unknown error occurred',
        'error'
      );
    } finally {
      setIsUpdating(false);
    }
  };

  const handleUpdateStoreUrl = async () => {
    if (!newStoreUrl) return;
    try {
      setIsUpdating(true);
      await entityService.updateEntityStoreUrl({ storeUrl: newStoreUrl });
      await refetch();
      setNewStoreUrl('');
      setShowUrlModal(false);
      toast.showToast('Store URL updated successfully.', 'success');
    } catch (error: any) {
      setShowUrlModal(false);
      console.error('Error updating store URL:', error);
      let errorMessage = 'Failed to update store URL. Please try again later.';
      
      // Handle specific error cases
      if (error?.message) {
        if (error.message.includes('already')) {
          errorMessage = 'This store URL is already taken. Please choose a different one.';
        } else if (error.message.includes('invalid')) {
          errorMessage = 'Invalid store URL. Please use only letters, numbers, and hyphens.';
        }
      }
      
      toast.showToast(errorMessage, 'error');
    } finally {
      setIsUpdating(false);
    }
  };

  const handleUpdateMobileNumber = async () => {
    if (!newMobileNumber) return;
    try {
      setIsUpdating(true);
      await entityService.updateEntityMobileNumber(newMobileNumber);
      await refetch();
      setNewMobileNumber('');
      setShowMobileModal(false);
      toast.showToast('Mobile number updated successfully.', 'success');
    } catch (error: any) {
      setShowMobileModal(false);
      console.error('Error updating mobile number:', error);
      toast.showToast('Failed to update mobile number. Please try again later.', 'error');
    } finally {
      setIsUpdating(false);
    }
  };

  const handleCancelEdit = (type: 'url' | 'mobile') => {
    if (type === 'url') {
      setIsEditingUrl(false);
      setNewStoreUrl('');
    } else {
      setIsEditingMobile(false);
      setNewMobileNumber('');
    }
  };

  if (userLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 px-4">
        <div className="flex items-center gap-3">
          <div className="animate-spin rounded-full h-6 w-6 border-2 border-blue-600 border-t-transparent"></div>
          <p className="text-gray-700 font-medium">Loading settings...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-4xl mx-auto px-4 py-6 sm:py-8">
        <div className="space-y-6 sm:space-y-8">
          {/* Store Status */}
          <div className="bg-white rounded-lg p-4 sm:p-6">
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center gap-3">
                <Store className="w-5 h-5 text-gray-500" />
                <h3 className="text-base sm:text-lg font-medium text-gray-900">Store Status</h3>
              </div>
              <button
                onClick={() => setShowStatusModal(true)}
                disabled={isUpdating}
                className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 ${entity?.isActive ? 'bg-blue-600' : 'bg-gray-200'
                }`}
              >
                <span
                  className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${entity?.isActive ? 'translate-x-6' : 'translate-x-1'
                  }`}
                />
              </button>
            </div>
            <p className="text-sm text-gray-600 ml-8">
              {entity?.isActive 
                ? 'Your store is currently active and visible to customers.'
                : 'Your store is currently inactive and hidden from customers.'}
            </p>
          </div>

          {/* Store URL */}
          <div className="bg-white rounded-lg p-4 sm:p-6">
            <div className="flex flex-col gap-4">
              <div className="flex items-start gap-3">
                <Link className="w-5 h-5 text-gray-500 mt-0.5" />
                <div className="flex-1">
                  <div className="flex items-center justify-between">
                  <h3 className="text-base sm:text-lg font-medium text-gray-900">Store URL</h3>
                    {!isEditingUrl && (
                      <button
                        onClick={() => setIsEditingUrl(true)}
                        className="text-sm text-blue-600 hover:text-blue-700 font-medium"
                      >
                        Edit
                      </button>
                    )}
                  </div>
                  {!isEditingUrl ? (
                  <div className="mt-2 flex items-center gap-1 text-sm">
                    <span className="text-gray-500">https://</span>
                    <span className="font-medium text-gray-900">{entity?.storeUrl}</span>
                    <span className="text-gray-500">.mart-24.com</span>
                  </div>
                  ) : (
                    <div className="mt-4 space-y-3">
              <div className="flex flex-col sm:flex-row gap-2">
                <input
                  type="text"
                  value={newStoreUrl}
                  onChange={(e) => setNewStoreUrl(e.target.value)}
                  placeholder="Enter new store URL"
                  className="flex-1 px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
                        <div className="flex gap-2">
                <button
                  onClick={() => setShowUrlModal(true)}
                  disabled={isUpdating || !newStoreUrl}
                            className="px-4 py-2 bg-blue-600 text-white text-sm font-medium rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed"
                          >
                            Save
                          </button>
                          <button
                            onClick={() => handleCancelEdit('url')}
                            className="px-4 py-2 text-gray-700 text-sm font-medium rounded-md hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-gray-500 focus:ring-offset-2"
                          >
                            Cancel
                          </button>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>

          {/* Contact Information */}
          <div className="bg-white rounded-lg p-4 sm:p-6">
            <div className="flex flex-col gap-4">
              <div className="flex items-start gap-3">
                <Phone className="w-5 h-5 text-gray-500 mt-0.5" />
                <div className="flex-1">
                  <div className="flex items-center justify-between">
                    <h3 className="text-base sm:text-lg font-medium text-gray-900">Mobile Number</h3>
                    {!isEditingMobile && (
                      <button
                        onClick={() => setIsEditingMobile(true)}
                        className="text-sm text-blue-600 hover:text-blue-700 font-medium"
                      >
                        Edit
                      </button>
                    )}
                  </div>
                  {!isEditingMobile ? (
                    <div className="mt-2 flex items-center gap-1 text-sm">
                      <span className="font-medium text-gray-900">{entity?.mobile || 'Not set'}</span>
                    </div>
                  ) : (
                    <div className="mt-4 space-y-3">
                      <div className="flex flex-col sm:flex-row gap-2">
                        <input
                          type="tel"
                          value={newMobileNumber}
                          onChange={(e) => setNewMobileNumber(e.target.value)}
                          placeholder="Enter new mobile number"
                          className="flex-1 px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        />
                        <div className="flex gap-2">
                          <button
                            onClick={() => setShowMobileModal(true)}
                            disabled={isUpdating || !newMobileNumber}
                            className="px-4 py-2 bg-blue-600 text-white text-sm font-medium rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                            Save
                          </button>
                          <button
                            onClick={() => handleCancelEdit('mobile')}
                            className="px-4 py-2 text-gray-700 text-sm font-medium rounded-md hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-gray-500 focus:ring-offset-2"
                          >
                            Cancel
                </button>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>

          {/* User Information */}
          <div className="bg-white rounded-lg p-4 sm:p-6">
            <div className="flex items-start gap-3 mb-4">
              <Mail className="w-5 h-5 text-gray-500 mt-0.5" />
              <div>
                <h3 className="text-base sm:text-lg font-medium text-gray-900">Login Information</h3>
              </div>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="p-3 bg-gray-50 rounded-md">
                <p className="text-xs font-medium text-gray-500 uppercase tracking-wider">Email</p>
                <p className="mt-1 text-sm text-gray-900 break-all">{entity?.email}</p>
              </div>
              <div className="p-3 bg-gray-50 rounded-md">
                <p className="text-xs font-medium text-gray-500 uppercase tracking-wider">Name</p>
                <p className="mt-1 text-sm text-gray-900">{entity?.name}</p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Status Toggle Confirmation Modal */}
      <ConfirmationModal
        isOpen={showStatusModal}
        onClose={() => setShowStatusModal(false)}
        onConfirm={handleToggleStoreStatus}
        title={entity?.isActive ? "Deactivate Store?" : "Activate Store?"}
        message={entity?.isActive 
          ? "Are you sure you want to deactivate your store? This will make it invisible to customers."
          : "Are you sure you want to activate your store? This will make it visible to customers."}
        confirmText={entity?.isActive ? "Deactivate" : "Activate"}
        cancelText="Cancel"
      />

      {/* URL Update Confirmation Modal */}
      <ConfirmationModal
        isOpen={showUrlModal}
        onClose={() => setShowUrlModal(false)}
        onConfirm={handleUpdateStoreUrl}
        title="Update Store URL?"
        message={`Are you sure you want to change your store URL to "${newStoreUrl}"? This will affect your store's web address.`}
        confirmText="Update URL"
        cancelText="Cancel"
      />

      {/* Mobile Number Update Confirmation Modal */}
      <ConfirmationModal
        isOpen={showMobileModal}
        onClose={() => setShowMobileModal(false)}
        onConfirm={handleUpdateMobileNumber}
        title="Update Mobile Number?"
        message={`Are you sure you want to change your mobile number to "${newMobileNumber}"?`}
        confirmText="Update Number"
        cancelText="Cancel"
      />
    </div>
  );
};

export default Settings; 